// Angular imports
import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';

// Import services
import { DataService } from '../services/data.service';
import { LoaderService } from '../loader/loader.service';
import { MessageService, MessageType } from '../services/message.service';

// Import types
import { Project } from '../types/project'

// Import subscription
import { BehaviorSubject, Subscription, combineLatest, debounceTime, distinctUntilChanged, tap, map, startWith } from 'rxjs';
import { CalculationState } from '../state-management/calculation-state';
import { Store } from '@ngrx/store';
import * as CalcualationActions from '../state-management/calculation-actions'
import { CalculationApi, CalculationMetaApi } from '../types';
import { ProjectCalculationService } from '../services/project-calculation/project-calculation.service';

declare type NestedCalculationApi = CalculationApi & {
    children?: NestedCalculationApi[];
};

@Component({
    selector: 'open-project',
    templateUrl: './open-project.component.html',
    styleUrls: ['./open-project.component.scss'],
    standalone: false
})
export class OpenProjectComponent implements OnInit, OnDestroy {
    @Output() public openProject = new EventEmitter<Project>();

    public searchBarSelected = false;

    private filterText$ = new BehaviorSubject<string>('');
    private dataServiceOnErrorSub: Subscription;

    public projFilterActive$ = new BehaviorSubject<boolean>(true);
    public projFilterClosed$ = new BehaviorSubject<boolean>(false);
    public projFilterCalculations$ = new BehaviorSubject<boolean>(false);

    public projects$ = combineLatest([
        this.projectCalculationService.projectList$,
        this.projFilterActive$.pipe(startWith(true)),
        this.projFilterClosed$.pipe(startWith(false)),
        this.projFilterCalculations$.pipe(startWith(false)),
        this.filterText$.pipe(
            startWith(''),
            debounceTime(200)
        )
    ])
        .pipe(
            distinctUntilChanged(),
            map(([projects, active, closed, calcs, filter]) =>
                projects
                    .filter(p =>
                        // Show only projects with calculation if option is selected
                        (!calcs || (!!p.calculations && !!p.calculations.length)) &&
                        // If active and closed are both false ignore them
                        ((!active && !closed && p.active) || ((active && p.active) || (closed && !p.active))) &&
                        // Filter on search text. Split up on spaces and match every word
                        filter.split(' ').every(e => p.id.includes(e) || p.description?.toLowerCase().includes(e))
                    )
                    .map(p => ({
                        ...p,
                        calculations: this.buildCalculationTree(p.calculations ?? [])
                    }))
            ),
            // tap((projects) => {
            //     console.log('OpenProjectComponent: projects$', projects);
            // })
        )

    constructor(
        private messageService: MessageService,
        private loader: LoaderService,
        private dataService: DataService,
        private store: Store<CalculationState>,
        private projectCalculationService: ProjectCalculationService,
    ) { }

    public ngOnInit() {
        console.log('OpenProjectComponent: ngOnInit');
        this.init();

        this.dataServiceOnErrorSub = this.dataService.onError.subscribe(err => {
            this.messageService.show({
                title: 'Fout',
                message: 'Er lijkt iets fout te zijn gegaan, de pagina wordt herladen.',
                type: MessageType.Error,
                buttons: [
                    {
                        text: 'Ok'
                    }
                ],
                closeHandler: () => {
                    // Reload the page
                    window.location.reload();
                }
            });
        });
    }

    // TODO: move to service, since exact same logic is used in multiple places (import lines)
    private buildCalculationTree(calculations: NestedCalculationApi[]): NestedCalculationApi[] {
        // Function to recursively build the tree structure
        const map = new Map<string, NestedCalculationApi>();
        const roots: NestedCalculationApi[] = [];

        calculations.forEach(calc => {
            map.set(calc.meta.id, { ...calc, children: [] });
        });

        calculations.forEach(calc => {
            if (calc.meta.parentCalculationId) {
                const parent = map.get(calc.meta.parentCalculationId);
                if (parent) {
                    parent.children.push(map.get(calc.meta.id)!);
                }
            } else {
                roots.push(map.get(calc.meta.id)!);
            }
        });

        return roots;
    }

    public ngOnDestroy() {
        this.dataServiceOnErrorSub.unsubscribe();
    }

    public async init() {
        // // Load the projects
        // this.loading = true;

        // // Skip the projects that only contain letters
        // this.totalProjectsList$.next(
        //     (await this.projectMetaDataService.getProjects())
        //         // Filter by id (ascending)
        //         .filter(f => /[0-9]/.test(f.id)).sort((a, b) => b.id < a.id ? -1 : 1)
        // );

        // this.loading = false;
    }

    public toggleFilterValue(filter: string) {
        switch (filter) {
            case 'isActive':
                this.projFilterActive$.next(!this.projFilterActive$.value);
                break;
            case 'isClosed':
                this.projFilterClosed$.next(!this.projFilterClosed$.value);
                break;
            case 'hasCalculation':
                this.projFilterCalculations$.next(!this.projFilterCalculations$.value);
                break;
        }
    }

    public async createCalculation(project: Project, type: string = 'stabu', event: MouseEvent) {
        // Stop bubbling up of event
        event.stopPropagation();

        if (!project.active) {
            return;
        }

        let message: string;

        switch (type) {
            case 'stabu':
                message = `Weet je zeker dat je een nieuwe calculatie wil aanmaken voor project '${project.description}'?`;
                break;
            case 'stabu-en':
                message = `Weet je zeker dat je een nieuwe Engelse calculatie wil aanmaken voor project '${project.description}'?`;
                break;
            case 'mmw':
                message = `Weet je zeker dat je een nieuwe meer-/minderwerk calculatie wil aanmaken voor project '${project.description}'?`;
                break;
            default:
                throw new Error('Invalid project type');
        }

        this.messageService.show({
            title: 'Let op',
            message: message,
            type: MessageType.Info,
            buttons: [
                {
                    text: 'Calculatie aanmaken',
                    handler: () => {
                        this.loader.performAsyncTask(async () => {
                            // State management solution
                            this.store.dispatch(CalcualationActions.createCalculation({ project: project, calculationType: type }));
                        });
                    }
                },
                {
                    text: 'Terug'
                }
            ]
        });
    }

    public async openCalculation(project: Project, calculation: CalculationApi) {
        this.store.dispatch(CalcualationActions.loadCalculation({
            projectId: project.id,
            calculationId: calculation.meta.id,
        }));
        // this.router.navigate(['project', project.id, 'calc', calculation.meta.id]);
    }

    // public async copyCalculation(project: Project, calculation: CalculationApi) {
    //     if (!project.active) {
    //         return;
    //     }

    //     this.messageService.show({
    //         title: 'Let op',
    //         message: `Weet je zeker dat je een kopie wilt maken van deze calculatie: '${project.description}'?`,
    //         type: MessageType.Info,
    //         buttons: [
    //             {
    //                 text: 'Kopie aanmaken',
    //                 handler: () => {
    //                     this.store.dispatch(CalcualationActions.copyCalculation({
    //                         projectId: project.id,
    //                         calculationId: calculation.meta.id
    //                     }))
    //                 }
    //             },
    //             {
    //                 text: 'Terug'
    //             }
    //         ]
    //     });
    // }

    public async createVariationsFromCalculation(project: Project, original: CalculationApi, event: MouseEvent) {
        // Stop bubbling up of event
        event.stopPropagation();

        this.messageService.show({
            title: 'Let op',
            message: `Weet je zeker dat je een nieuwe meer-/minderwerk calculatie wilt aanmaken op basis van deze calculatie?`,
            type: MessageType.Info,
            buttons: [
                {
                    text: 'Meer-/minderwerk aanmaken',
                    handler: () => {
                        this.store.dispatch(CalcualationActions.variationsFromCalculation({
                            projectId: project.id,
                            calculationId: original.meta.id
                        }));
                    }
                },
                {
                    text: 'Terug'
                }
            ]
        });
    }

    public focusInput() {
        this.searchBarSelected = true;
    }

    public blurInput() {
        this.searchBarSelected = false;
    }

    public searchInputChange(search: string) {
        this.filterText$.next(search.toLowerCase());
    }
}
